import {
  LAB_PREVIEW_ID,
  previewLabOrders,
  previewLabReport,
  useLabPreview,
} from "@/features/labs/labPreview";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth/AuthProvider";
import type {
  LabCatalog,
  LabCheckoutInput,
  LabCheckoutResponse,
  LabCheckoutStatus,
  LabOrder,
  LabOrdersResponse,
  LabReport,
  LabSubscription,
} from "@/types/labs";
import { ApiError, apiFetch } from "./client";

export function useLabCatalog(enabled = true) {
  const { session } = useAuth();
  return useQuery({
    queryKey: ["labs", "catalog", session?.user.id],
    queryFn: () => apiFetch<LabCatalog>("/labs/catalog"),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

export function useLabOrders(enabled = true) {
  const preview = useLabPreview();
  const { session } = useAuth();
  return useQuery({
    queryKey: ["me", "lab-orders", session?.user.id, preview.stage],
    queryFn: () =>
      preview.stage !== "actual"
        ? Promise.resolve({
            enabled: true,
            orders: previewLabOrders(preview.stage),
          })
        : apiFetch<LabOrdersResponse>("/me/lab-orders"),
    enabled: enabled && Boolean(session?.user.id),
    refetchInterval: 30_000,
    retry: false,
  });
}

export function useLabOrder(id: string | undefined) {
  const preview = useLabPreview();
  const { session } = useAuth();
  return useQuery({
    queryKey: ["me", "lab-orders", session?.user.id, id, preview.stage],
    queryFn: () =>
      (id === LAB_PREVIEW_ID || id === `${LAB_PREVIEW_ID}-previous`) &&
      preview.stage !== "actual" &&
      preview.stage !== "empty"
        ? Promise.resolve(
            previewLabOrders(preview.stage).find((order) => order.id === id)!,
          )
        : apiFetch<LabOrder>(`/me/lab-orders/${encodeURIComponent(id!)}`),
    enabled: Boolean(session?.user.id && id),
    refetchInterval: 30_000,
    retry: false,
  });
}

export function useLabReport(
  orderId: string,
  resultId: string | null,
  clinician = false,
  reportIndex = 0,
  adminPatientId?: string,
) {
  const preview = useLabPreview();
  const { session } = useAuth();
  return useQuery({
    queryKey: [
      adminPatientId ? "admin" : clinician ? "clinician" : "me",
      "lab-report",
      adminPatientId ?? null,
      session?.user.id,
      orderId,
      resultId,
      reportIndex,
      preview.stage,
    ],
    queryFn: () =>
      orderId === LAB_PREVIEW_ID &&
      preview.stage === "resultsReady" &&
      !clinician &&
      !adminPatientId
        ? Promise.resolve(previewLabReport())
        : apiFetch<LabReport>(
            `${adminPatientId ? `/admin/clinical-admin/patients/${encodeURIComponent(adminPatientId)}` : clinician ? "/clinician" : "/me"}/lab-orders/${encodeURIComponent(orderId)}/report?result_id=${encodeURIComponent(resultId!)}${reportIndex ? `&report_index=${reportIndex}` : ""}`,
          ),
    enabled: Boolean(session?.user.id && resultId),
    retry: false,
    gcTime: 0,
  });
}

/** Fresh, owner-authorized URL on each click; never cache signed report links. */
export function useLabPdf(
  orderId: string,
  resultId: string,
  reportIndex: number,
  clinician: boolean,
  adminPatientId?: string,
) {
  const preview = useLabPreview();
  return useMutation({
    mutationFn: () =>
      orderId === LAB_PREVIEW_ID &&
      preview.stage === "resultsReady" &&
      !clinician &&
      !adminPatientId
        ? Promise.resolve<LabReport>({
            title: "Synthetic example report",
            content_type: "application/pdf",
            download_url: new URL(
              "/images/tasso/example-lab-report.pdf",
              window.location.origin,
            ).href,
          })
        : apiFetch<LabReport>(
            `${adminPatientId ? `/admin/clinical-admin/patients/${encodeURIComponent(adminPatientId)}` : clinician ? "/clinician" : "/me"}/lab-orders/${encodeURIComponent(orderId)}/report?result_id=${encodeURIComponent(resultId)}&report_index=${reportIndex}&download_pdf=true`,
          ),
    retry: false,
    gcTime: 0,
  });
}

export function useCreateLabCheckout() {
  return useMutation({
    mutationFn: (body: LabCheckoutInput) =>
      apiFetch<LabCheckoutResponse>("/labs/checkout", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    retry: false,
  });
}

export function useLabCheckoutStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      checkout_id?: string;
      request_id?: string;
      checkout_token: string;
    }) =>
      apiFetch<LabCheckoutStatus>("/labs/checkout/status", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me", "lab-orders"] });
    },
    // This endpoint reconciles one immutable checkout. A disconnected
    // post-confirmation request must not send a paid patient back to payment.
    // Retry only transport/transient failures, never an auth/ownership refusal.
    retry: (failureCount, error) =>
      failureCount < 2 &&
      (error instanceof ApiError
        ? error.status >= 500 || [408, 429, 499].includes(error.status)
        : error instanceof TypeError || error.name === "AbortError"),
    retryDelay: (attempt) => Math.min(1_000 * 2 ** attempt, 2_000),
  });
}

/** Owned billing remains manageable in Account after an experiment cohort changes. */
export function useLabSubscriptions() {
  const { session } = useAuth();
  return useQuery({
    queryKey: ["me", "lab-subscriptions", session?.user.id],
    queryFn: () =>
      apiFetch<{ subscriptions: LabSubscription[] }>("/me/lab-subscriptions"),
    enabled: Boolean(session?.user.id),
    retry: false,
  });
}

export function useCancelLabSubscription() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/me/lab-subscriptions/${encodeURIComponent(id)}/cancel`, {
        method: "POST",
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["me", "lab-subscriptions"] });
    },
  });
}
